import { zodResolver } from '@hookform/resolvers/zod';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Alert, CircularProgress, IconButton, Snackbar } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { LOGIN_PAGE_TEXT } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import './LoginPage.scss';

interface LocationState {
  from?: { pathname: string };
  message?: string;
}

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, error, clearError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const [showPassword, setShowPassword] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;
    interface Particle {
      x: number;
      y: number;
      r: number;
      dx: number;
      dy: number;
      o: number;
    }
    const rand = (a: number, b: number) => Math.random() * (b - a) + a;
    let particles: Particle[] = [];
    let raf = 0;

    const resize = () => {
      const parent = canvas.parentElement!;
      canvas.width = parent.offsetWidth || window.innerWidth / 2;
      canvas.height = parent.offsetHeight || window.innerHeight;
      particles = Array.from({ length: 40 }, () => ({
        x: rand(0, canvas.width),
        y: rand(0, canvas.height),
        r: rand(1, 3.5),
        dx: rand(-0.3, 0.3),
        dy: rand(-0.25, 0.25),
        o: rand(0.1, 0.45),
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.o})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      }
      raf = requestAnimationFrame(draw);
    };

    const start = () => {
      resize();
      draw();
    };
    const timer = setTimeout(start, 50);

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      window.removeEventListener('resize', resize);
    };
  }, []);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (isAuthenticated) {
      const state = location.state as LocationState | null;
      const from = state?.from?.pathname || '/dashboard';
      // Employees always land on dashboard; other roles return to where they came from
      const destination = user?.role === 'employee' ? '/dashboard' : from;
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, location, navigate, user]);

  useEffect(() => {
    if (error) clearError();
    // Run-once-on-mount: clears a stale error left over from a previous visit
    // to this page. Adding `error`/`clearError` (as the linter suggests) would
    // also re-run this effect the instant `error` is set by a failed login,
    // immediately clearing the message we just showed the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values: LoginFormValues) => {
    setLoading(true);

    try {
      await login({
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';

      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoading && !isSubmitting) {
    return (
      <div className="lp-loading">
        <CircularProgress className="lp-spinner-loading" />
      </div>
    );
  }

  return (
    <div className="lp-root" data-testid="login-page">
      {/* ── Left panel — hero image ── */}
      <div className="lp-left">
        <img src="/assets/login/hrms-hero.png" alt={LOGIN_PAGE_TEXT.heroImageAlt} className="lp-hero-img" />
      </div>

      {/* ── Right panel ── */}
      <div className="lp-right">
        <canvas ref={canvasRef} className="lp-particles-canvas" />
        <div className="lp-form-card">
          <div className="lp-form-header">
            <h2 className="lp-form-title">{LOGIN_PAGE_TEXT.welcomeTitle}</h2>
            <p className="lp-form-subtitle">{LOGIN_PAGE_TEXT.welcomeSubtitle}</p>
          </div>

          {error && (
            <div className="lp-alert lp-alert-error" data-testid="error-message">
              <span className="material-symbols-outlined">error</span>
              <span>{error}</span>
            </div>
          )}
          {(location.state as LocationState | null)?.message && (
            <div className="lp-alert lp-alert-success">
              <span className="material-symbols-outlined">check_circle</span>
              <span>{(location.state as LocationState | null)?.message}</span>
            </div>
          )}

          <form className="lp-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Email */}
            <div className={`lp-field${formErrors.email ? ' lp-field-error' : ''}`}>
              <label className="lp-label" htmlFor="lp-email">
                {LOGIN_PAGE_TEXT.emailLabel}
              </label>
              <div className="lp-input-wrap">
                <span className="material-symbols-outlined lp-input-icon">mail</span>
                <input
                  id="lp-email"
                  className="lp-input"
                  type="email"
                  placeholder={LOGIN_PAGE_TEXT.emailPlaceholder}
                  autoComplete="email"
                  autoFocus
                  data-testid="email-input"
                  {...register('email')}
                />
              </div>
              {formErrors.email && (
                <span className="lp-field-msg" data-testid="email-error">
                  {formErrors.email.message}
                </span>
              )}
            </div>

            {/* Password */}
            <div className={`lp-field${formErrors.password ? ' lp-field-error' : ''}`}>
              <label className="lp-label" htmlFor="lp-password">
                {LOGIN_PAGE_TEXT.passwordLabel}
              </label>
              <div className="lp-input-wrap">
                <span className="material-symbols-outlined lp-input-icon">lock</span>
                <input
                  id="lp-password"
                  className="lp-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={LOGIN_PAGE_TEXT.passwordPlaceholder}
                  autoComplete="current-password"
                  data-testid="password-input"
                  {...register('password')}
                />
                <IconButton
                  className="lp-pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label="toggle password visibility"
                  size="small"
                  data-testid="toggle-password-visibility-button"
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </div>
              {formErrors.password && (
                <span className="lp-field-msg" data-testid="password-error">
                  {formErrors.password.message}
                </span>
              )}
            </div>

            {/* Remember me + forgot password */}
            <div className="lp-form-row">
              <label className="lp-check-label">
                <input
                  className="lp-check"
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  {...register('rememberMe')}
                />
                <span>{LOGIN_PAGE_TEXT.rememberMeLabel}</span>
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="lp-submit"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? <CircularProgress size={20} className="lp-spinner-submit" /> : LOGIN_PAGE_TEXT.signInButton}
            </button>

            <div className="lp-generate-row">
              <Link className="lp-link" to="/auth/forgot-password?type=generate" data-testid="generate-password-link">
                {LOGIN_PAGE_TEXT.generatePasswordLink}
              </Link>
            </div>
          </form>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};